import { context } from "./context";
import { http } from "./http";
import { createEventEmitter, EventEmitter } from "./utils";
import { auth } from "./auth";
import { AppInfo, AppSettings, TenantApps, AppModuleInfo, JITUXInfo, RemoveDomFunc, jituxDashboardAppId } from "../models";
import { JituxProxy } from "./jitux-proxy";
import Config from "./config";

export class AppStore {
  _apps: Array<AppInfo> = [];
  _changedEvent = createEventEmitter();
  _pingIntervalMs = 30000;
  private _pings: { [key: string]: any } = {};
  private _clientDashboardId: number;
  private _appInfoRequestId: number = 0;

  constructor() {
    const selectedDashboardId = context.get("clientDashboardId");
    this._clientDashboardId = Number.isInteger(selectedDashboardId) && selectedDashboardId >= 0
      ? selectedDashboardId
      : 0;
    context.changedEvent.subscribe((key: string, value: any) => {
      if (key === "identity" && !value) {
        this._clientDashboardId = 0;
        context.set("clientDashboardId", 0);
        this._appInfoRequestId++;
      }
    });
  }

  get apps(): Array<AppInfo> {
    return this._apps;
  }
  get clientDashboardId(): number {
    return this._clientDashboardId === undefined ? 0 : this._clientDashboardId;
  }
  selectDashboard(clientDashboardId: number): void {
    if (!Number.isInteger(clientDashboardId) || clientDashboardId < 0) {
      throw new Error("Invalid clientDashboardId");
    }
    this._clientDashboardId = clientDashboardId;
    context.set("clientDashboardId", clientDashboardId);
  }
  set apps(value: Array<AppInfo>) {
    this._apps = value.sort(this.compareAppFunc);
    context.set("apps", this._apps);
  }

  get changedEvent(): EventEmitter {
    return this._changedEvent;
  }

  private getAppKey(app: AppInfo): string {
    return JSON.stringify([app.id, Number(app.appConfigurations?.clientDashboardId ?? 0)]);
  }

  checkAlive(): void {
    const updateApp = (app: AppInfo, alive: boolean) => {
      if (!this._apps.includes(app)) return;
      Object.assign(app, { alive });
      this.apps = [...this._apps];
      this._changedEvent.emit(app);
    };

    this._apps.forEach(app => {
      const key = this.getAppKey(app);
      if (this._pings[key] == null || Date.now() - this._pings[key] > this._pingIntervalMs) {
        if (app.appConfigurations?.cardMode == 'sso' || app.appConfigurations?.cardMode == 'url'
          || app.appConfigurations?.cardMode == 'jitux'
        ) {
          updateApp(app, true);
        } else {
          this.ping(app)
            .then(result => {
              updateApp(app, result);
            })
            .catch(_ => {
              updateApp(app, false);
              console.warn(app.id + " not reachable");
            });
        }
        this._pings[key] = Date.now();
      }
    });
  }

  compareAppFunc(a: AppInfo, b: AppInfo): number {
    if (a.displayOrder != b.displayOrder) return a.displayOrder > b.displayOrder ? 1 : -1;
    return a.displayName > b.displayName ? 1 : -1;
  }

  createDOMTag(parentId: string, uniqueTagName: string): RemoveDomFunc {
    const parent = document.getElementById(parentId);
    if (parent) {
      if (!document.getElementById(uniqueTagName)) {
        const elem: any = document.createElement(uniqueTagName);
        elem.id = uniqueTagName;
        parent.appendChild(elem);
        return () => {
          if (parent.contains(elem)) parent.removeChild(elem);
        };
      }
    }
    return () => {};
  }

  // Helper method to load modules with either Vite or SystemJS based on app configuration
  private loadModule(app: AppInfo, url: string): Promise<any> {
    // Check if app is configured to use Vite
    if (app.appConfigurations?.buildTool === 'vite') {
      const baseUrl = app.resourceBaseUrl+"/Jitux.js";//url.split('?')[0]; // Remove query params for Vite
      console.log(`Loading Vite module from: ${baseUrl}`);
      
      return import(/* webpackIgnore: true */ baseUrl)
        .then(module => {
          console.log(`Successfully loaded Vite module for ${app.id}:`, module);
          return module;
        })
        .catch(error => {
          console.error(`Error importing Vite module from ${baseUrl}:`, error);
          throw error;
        });
    } else if (window["SystemJS"]) {
      console.log(`Loading SystemJS module from: ${url}`);
      return window["SystemJS"].import(url);
    } else {
      return Promise.reject(new Error(`No module loader available for app: ${app.id}`));
    }
  }

  loadAppCard(app: AppInfo): Promise<AppInfo> {
    const url = app.cardBundleUrl + `?v=${app.version || "0"}`;
    return this.loadModule(app, url)
      .then((module: any) => {
        // For Vite apps, try to use JituxCard component
        const isViteApp = app.appConfigurations?.buildTool === 'vite';
        const appCardModule = isViteApp && module.JituxCard ? module.JituxCard : module;
        
        if (!appCardModule || typeof appCardModule.mount !== 'function') {
          throw new Error(`Card component not found or invalid in module for app ${app.id}`);
        }
        
        let appMetaData = {
          domTagName: `card-${app.id}`
        };
        
        if (typeof appCardModule.getMetadata === "function") {
          appMetaData = Object.assign(appMetaData, appCardModule.getMetadata());
        }

        app.cardModule = appCardModule;
        app.cardMount = (mountDomId: string, context: any) => {
          const jitux: JITUXInfo = this.getJituxInfo(context, app);
          const deleteDOMTag: RemoveDomFunc = this.createDOMTag(mountDomId, appMetaData.domTagName);
          appCardModule.mount(jitux, appMetaData.domTagName);
          return () => {
            appCardModule.unmount(appMetaData.domTagName);
            if (typeof deleteDOMTag === "function") deleteDOMTag();
          };
        };
        return app;
      })
      .then((app: AppInfo) => this.updateApp(app));
  }

  loadApp(app: AppInfo): Promise<AppInfo> {
    const url = app.bundleUrl + `?v=${app.version || "0"}`;
    return this.loadModule(app, url)
      .then((module: any) => {
        // For Vite apps, try to use JituxApp component
        const isViteApp = app.appConfigurations?.buildTool === 'vite';
        const appModule = isViteApp && module.JituxApp ? module.JituxApp : module;
        
        if (!appModule || typeof appModule.mount !== 'function') {
          throw new Error(`App component not found or invalid in module for app ${app.id}`);
        }
        
        let appMetaData = {
          domTagName: app.domTagName || `app-root`,
          baseRoute: app.baseRoute || (app.id === jituxDashboardAppId ? "" : app.id)
        };
        
        if (typeof appModule.getMetadata === "function") {
          appMetaData = Object.assign(appMetaData, appModule.getMetadata());
        }

        app = {
          ...app,
          ...appMetaData,
          module: appModule,
          mount: (mountDomId: string, context: any) => {
            const jitux: JITUXInfo = this.getJituxInfo(context, app);
            const deleteDOMTag: RemoveDomFunc = this.createDOMTag(mountDomId, appMetaData.domTagName);
            appModule.mount(jitux, appMetaData.domTagName);
            return () => {
              appModule.unmount(appMetaData.domTagName);
              if (typeof deleteDOMTag === "function") deleteDOMTag();
            };
          }
        };
        return app;
      })
      .then((app: AppInfo) => this.updateApp(app));
  }

  loadAppInfo(signal?: AbortSignal): Promise<void> {
    const requestId = ++this._appInfoRequestId;
    const options: RequestInit = { headers: new Headers({ "Content-Type": "application/json" }), signal };
    const configuredAppUrl = Config.getOptional("appUrl");
    const appUrl = typeof configuredAppUrl === "string" && configuredAppUrl
      ? configuredAppUrl
      : "api/app";
    return http.fetch(appUrl, options, true).then((result: TenantApps) => {
      if (signal?.aborted || requestId !== this._appInfoRequestId) {
        const error = new Error("Dashboard request was canceled or superseded");
        error.name = "AbortError";
        throw error;
      }
      if (!result || !Array.isArray(result.appsInfo) || result.appsInfo.some(app => !app || typeof app.id !== "string")) {
        throw new Error("Invalid dashboard apps response");
      }
      const apps = result.appsInfo.map(app => {
        const resourceBaseUrl = typeof app.resourceBaseUrl === "string"
          ? app.resourceBaseUrl.replace(/\/+$/, "")
          : "";
        if (!app.bundleUrl && resourceBaseUrl) app.bundleUrl = `${resourceBaseUrl}/${app.id}.js`;
        if (!app.cardBundleUrl && resourceBaseUrl) app.cardBundleUrl = `${resourceBaseUrl}/${app.id}.card.js`;
        const key = this.getAppKey(app);
        const existing = this._apps.find(item => this.getAppKey(item) === key);
        // Keep loaded modules only when their bundle definitions have not changed.
        if (existing && existing.version === app.version && existing.bundleUrl === app.bundleUrl &&
          existing.cardBundleUrl === app.cardBundleUrl && existing.resourceBaseUrl === app.resourceBaseUrl) {
          return Object.assign(existing, app);
        }
        return app;
      });
      const dashboard = this._apps.find(app => app.id === jituxDashboardAppId);
      if (dashboard && !apps.some(app => app.id === jituxDashboardAppId)) apps.push(dashboard);
      this._pings = {};
      this.apps = apps;
      this.checkAlive();
      if (result.organization) window.sessionStorage.setItem("organizationId", result.organization);
    });
  }

  loadAppSettings(): Promise<AppSettings> {
    return http.fetch("api/app/settings").then((result: AppSettings) => {
      return result;
    });
  }
  // TODO: Currently the logic of mounting cards or apps is implemented in `csg-jitux-context` library.
  // We need to move that logic back to JIT-UX, for centralizing the implementation.
  /* 
  mountAppCard(appId: string, domId: string): Promise<CardUnmountFunc> {
    return new Promise((resolve, reject) => {
      const app = this.apps.find(x => x.id === appId);
      if (app) {
        const mount = (x: AppInfo) => Card.render(domId, app, (bodyDomId: string) => x.cardMount(bodyDomId, context));
        if (!app.cardMount) {
          // Check whether the card bundle exists before loading.
          http.fetch(app.cardBundleUrl, { method: "HEAD" }).then((result: any) => {
            if (result && result.ok) {
              this.loadAppCard(app)
                .then((_: any) => {
                  resolve(mount(app));
                })
                .catch((err: any) => {
                  console.error(`Unable to load card for '${app.id}'. Default card will be displayed`, err);
                  resolve(Card.render(domId, app));
                });
            } else resolve(Card.render(domId, app));
          });
        } else {
          resolve(mount(app));
        }
      } else {
        messageHandler.handle({
          type: UserMessageType.Warning,
          message: UserMessages.UNEXPECTED_ERROR,
          detail: `App card failed to mount - ${appId} not found`
        });
        reject();
      }
    });
  }
  */

  updateApp(app: AppInfo): AppInfo {
    const key = this.getAppKey(app);
    this.apps = [...this.apps.filter(x => this.getAppKey(x) !== key), app];
    this.checkAlive();
    return app;
  }

  ping(app: AppInfo): Promise<boolean> {
    // Use "HEAD" method because we don't really want to get the big bundle.
    return http.fetch(app.bundleUrl, { method: "HEAD" }).then(result => !!(result && result.ok));
  }

  private getJituxInfo(context: any, app: AppInfo): JITUXInfo {
    return {
      context,
      app,
      apps: this.apps,
      addAuthHeader: (req: any) => auth.addAuthHeader(req),
      reauthenticate: () => auth.reauthenticate(),
      proxy: new JituxProxy(app)
    };
  }
}

export const appStore = new AppStore();